import { useCallback, useEffect, useMemo, useState } from "react";

import { Table } from "@mantine/core";
import { IconDownload, IconEdit, IconFileFilled, IconX } from "@tabler/icons-react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Text } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";

import { CreditRequestAttachmentResponse, CreditResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import AttachmentDeleteModal from "./delete";
import AttachmentUpdateModal from "./edit";

const { ROLE_CR_ATT } = AuthorityConstants;
const { REQUEST_CREDIT_ATTACHMENT_UPDATE_ID, REQUEST_CREDIT_ATTACHMENT_DELETE_ID } =
  ServiceConstants;

interface CreditAttachmentDocumentationItemProps {
  credit: CreditResponse;
  item: CreditRequestAttachmentResponse;
  updateAttachment: (attachment: CreditRequestAttachmentResponse) => void;
  removeAttachment: (id: string) => void;
  isEditAllowed: boolean;
}
const CreditAttachmentDocumentationItem = ({
  credit,
  item,
  updateAttachment,
  removeAttachment,
  isEditAllowed,
}: CreditAttachmentDocumentationItemProps) => {
  const { t } = useTranslation();

  const updateServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_UPDATE_ID + item.id],
  );
  const deleteServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_DELETE_ID + item.id],
  );

  const [editView, setEditView] = useState<boolean>(false);
  const [deleteView, setDeleteView] = useState<boolean>(false);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      updateServiceObject,
      REQUEST_CREDIT_ATTACHMENT_UPDATE_ID + item.id,
      (val) => {
        updateAttachment(val);
        setEditView(false);
      },
    );
    AppUtils.resolveServiceObject(
      deleteServiceObject,
      REQUEST_CREDIT_ATTACHMENT_DELETE_ID + item.id,
      () => {
        removeAttachment(item.id);
        setDeleteView(false);
      },
    );
  }, [updateServiceObject, deleteServiceObject]);

  const fileSize = useMemo(() => {
    if (Math.round(item.size / 1024 / 1024) > 0) {
      return Math.round(item.size / 1024 / 1024) + "MB";
    }
    if (Math.round(item.size / 1024) > 0) {
      return Math.round(item.size / 1024) + "KB";
    }
    if (Math.round(item.size) > 0) {
      return Math.round(item.size) + "B";
    }
  }, [item]);

  const preview = useCallback(() => {
    window.open(
      `${process.env.REACT_APP_API_URL_ATTACHMENTS}/credit-request/${credit.id}/${item.id}.${item.extension}`,
      "_blank",
    );
  }, []);

  // const downloadFile = useCallback(() => {
  //   const link = document.createElement('a');
  //   link.href = `${process.env.REACT_APP_API_URL_ATTACHMENTS}/credit-request/${credit.id}/${item.id}.${item.extension}`;
  //   link.download = item.name + '.' + item.extension;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // }, []);

  return (
    <Table.Tr key={`credit-attachment-${item.id}`}>
      <Table.Td w={"5%"}>
        <IconFileFilled color={"var(--mantine-color-primary-6)"} size={40} onClick={preview} />
      </Table.Td>
      <Table.Td w={"20%"}>
        <Text>{item.name}</Text>
        <Text size={"xs"}>{fileSize}</Text>
      </Table.Td>
      <Table.Td w={"20%"}>{item.info.createdBy.fullName}</Table.Td>
      <Table.Td w={"25%"}>
        {t("format.date_time2", { date: new Date(item.info.createdAt) })}
      </Table.Td>
      <Table.Td w={"30%"} align={"right"}>
        <Button variant={"transparent"} size={"compact-sm"} onClick={preview}>
          <IconDownload color={"var(--mantine-color-primary-6)"} />
        </Button>
        {isEditAllowed && UserUtils.hasAuthority(ROLE_CR_ATT) && (
          <>
            <Button variant={"transparent"} size={"compact-sm"} onClick={() => setEditView(true)}>
              <IconEdit color={"var(--mantine-color-gray-7)"} />
            </Button>
            <AttachmentUpdateModal
              item={item}
              opened={editView}
              onClose={() => setEditView(false)}
              t={t}
            />
            <Button variant={"transparent"} size={"compact-sm"} onClick={() => setDeleteView(true)}>
              <IconX color={"var(--mantine-color-error)"} />
            </Button>
            <AttachmentDeleteModal
              item={item}
              opened={deleteView}
              onClose={() => setDeleteView(false)}
              t={t}
            />
          </>
        )}
      </Table.Td>
    </Table.Tr>
  );
};

export default CreditAttachmentDocumentationItem;
